/**
 * One station alert (step 7): what is out and where (the condition, the station, the machine's tag), the level, the
 * value against the window and the stop limit, how long it has been out, whether it has gone stale, what it puts at
 * risk, how far it has stepped up unacknowledged, and who acknowledged it and how; for a member of the station,
 * Acknowledge. Pure; StationAlertsSection holds its state.
 */
import { Link } from "react-router-dom";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import type { StationAlert, StationAlertAckKind } from "@/lib/api/station-alerts";
import {
  ackText,
  alertAckable,
  alertLevelLook,
  alertStateText,
  alertValueLine,
  escalationText,
  outForText,
} from "@/lib/plant/station-alerts";
import { AckForm } from "./AckForm";

export interface AlertCardProps {
  alert: StationAlert;
  /** The plant's zone, for the time a stale alert's last value came. */
  zone?: string;
  /** The acknowledgement being written; null while it is not. */
  ack: { kind: StationAlertAckKind | ""; detail: string } | null;
  saving: boolean;
  error: string | null;
  onAck: () => void;
  onKind: (k: StationAlertAckKind) => void;
  onDetail: (d: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function AlertCard(p: AlertCardProps) {
  const a = p.alert;
  const look = alertLevelLook(a);
  const named = `alert-${a.id}-headline`;
  const acked = ackText(a);
  const stepped = escalationText(a);
  const cleared = a.state === "cleared";
  return (
    <li
      data-alert={a.id}
      data-state={a.state}
      className="flex flex-col gap-1.5 rounded-md border border-l-4 border-border border-l-danger bg-surface-elevated px-3 py-2 text-sm"
    >
      <div className="flex items-start justify-between gap-2">
        <p id={named} className="min-w-0 font-medium text-on-surface">
          {a.label || a.condition_key} at{" "}
          <Link to={`/app/stations/${encodeURIComponent(a.station_id)}`} className="text-brand hover:underline">
            {a.station_name}
          </Link>
          {a.tag_name ? <span className="font-normal text-on-surface-muted"> ({a.tag_name})</span> : null}
        </p>
        <Badge variant={cleared ? "secondary" : look.tone} className="shrink-0 text-[11px]">
          {cleared ? "Cleared" : look.word}
        </Badge>
      </div>
      <p className="text-on-surface">{alertValueLine(a)}</p>
      <p className="text-xs text-on-surface-muted">
        {alertStateText(a, p.zone)}. {cleared ? "Was out" : "Out"} for {outForText(a.out_for_min)}
        {a.reraised > 0 ? `; back out ${a.reraised} ${a.reraised === 1 ? "time" : "times"} since it first cleared` : ""}.
      </p>
      {a.at_risk && <p className="text-xs text-on-surface-muted">Puts at risk: {a.at_risk}</p>}
      {stepped && (
        <p className={stepped.urgent ? "text-xs font-medium text-danger" : "text-xs text-on-surface-muted"}>{stepped.text}</p>
      )}
      {acked && <p className="text-xs text-on-surface">{acked}</p>}
      {p.ack ? (
        <AckForm
          kind={p.ack.kind}
          detail={p.ack.detail}
          saving={p.saving}
          error={p.error}
          describedBy={named}
          onKind={p.onKind}
          onDetail={p.onDetail}
          onSave={p.onSave}
          onCancel={p.onCancel}
        />
      ) : (
        a.can_ack &&
        alertAckable(a) && (
          <div className="flex justify-end">
            <Button type="button" size="sm" onClick={p.onAck} aria-describedby={named}>
              Acknowledge
            </Button>
          </div>
        )
      )}
      {!p.ack && p.error && (
        <p role="alert" className="text-sm text-danger">
          {p.error}
        </p>
      )}
    </li>
  );
}
